// ABOUTME: The `filters` URL parameter that opens a list already filtered, e.g. from a chart slice click.
// ABOUTME: A JSON `{ label, filters }`: search filters applied to the list's search, and the label of the chip naming them.

import { type SearchFilter, SearchFilterOperator } from '@src/__generated__/types';

export const LIST_FILTERS_PARAM = 'filters';

export type ListFilters = {
    // What the filters stand for, shown on the chip above the list (e.g. "Quotes by status: Draft").
    label?: string;
    filters: SearchFilter[];
};

const OPERATORS = Object.values(SearchFilterOperator) as string[];

const isSearchFilter = (raw: unknown): raw is SearchFilter => {
    if (typeof raw !== 'object' || raw === null) return false;
    const { field, operator, value } = raw as Record<string, unknown>;
    return (
        typeof field === 'string' &&
        typeof operator === 'string' &&
        OPERATORS.includes(operator) &&
        Array.isArray(value) &&
        value.every(v => typeof v === 'string')
    );
};

/** The filters of the parameter; null when it is absent or malformed, or holds no valid filter. */
export function parseListFilters(raw: string | null): ListFilters | null {
    if (!raw) return null;
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { label, filters } = parsed as Record<string, unknown>;
    const valid = Array.isArray(filters) ? filters.filter(isSearchFilter) : [];
    if (valid.length === 0) return null;
    return {
        ...(typeof label === 'string' && label.trim() ? { label } : {}),
        filters: valid.map(({ field, operator, value }) => ({ field, operator, value })),
    };
}

/** `path` with the parameter set, keeping the query and hash it already has. */
export function buildListFiltersPath(path: string, listFilters: ListFilters): string {
    const hashIndex = path.indexOf('#');
    const hash = hashIndex >= 0 ? path.slice(hashIndex) : '';
    const withoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
    const [pathname, query = ''] = withoutHash.split('?');
    const params = new URLSearchParams(query);
    params.set(LIST_FILTERS_PARAM, JSON.stringify(listFilters));
    return `${pathname}?${params.toString()}${hash}`;
}
